import React, { useState, useEffect } from 'react';
import { gdprV5API } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Shield, Plus, FileText, Trash2, Download, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = { pending: '#9A6B1A', processing: '#1E5A8A', completed: '#1F7A5A', rejected: '#B3261E' };
const TYPE_LABELS = { access: 'Data Access', erasure: 'Right to Erasure', portability: 'Data Portability', rectification: 'Rectification', restriction: 'Processing Restriction' };

export default function AdminGDPRFull() {
  const [dsars, setDsars] = useState([]);
  const [retention, setRetention] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProcess, setShowProcess] = useState(null);
  const [retentionForm, setRetentionForm] = useState({});
  const [savingRetention, setSavingRetention] = useState(false);
  const [purging, setPurging] = useState(false);
  const [processForm, setProcessForm] = useState({ status: 'completed', notes: '' });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [dsarRes, retRes] = await Promise.all([
        gdprV5API.listDSARs().catch(() => ({ data: { requests: [] } })),
        gdprV5API.getRetention().catch(() => ({ data: {} })),
      ]);
      setDsars(dsarRes.data.requests || []);
      setRetention(retRes.data);
      setRetentionForm(retRes.data);
    } catch { toast.error('Failed to load GDPR data'); }
    finally { setLoading(false); }
  };

  const processDSAR = async () => {
    try {
      await gdprV5API.processDSAR(showProcess.id, processForm);
      toast.success(`DSAR ${processForm.status}`);
      setShowProcess(null);
      loadAll();
    } catch { toast.error('Failed to process'); }
  };

  const saveRetention = async () => {
    setSavingRetention(true);
    try {
      await gdprV5API.setRetention(retentionForm);
      toast.success('Retention policy saved');
      loadAll();
    } catch { toast.error('Failed'); }
    finally { setSavingRetention(false); }
  };

  const purgeExpired = async () => {
    if (!window.confirm('Purge all expired data? This cannot be undone.')) return;
    setPurging(true);
    try {
      const r = await gdprV5API.purgeExpired();
      const purged = r.data.purged || {};
      toast.success(`Purged: ${Object.entries(purged).map(([k,v]) => `${v} ${k}`).join(', ') || 'nothing to purge'}`);
    } catch { toast.error('Purge failed'); }
    finally { setPurging(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Shield className="w-6 h-6 text-[#B89B5E]" /> GDPR / Privacy Management</h1>
        <p className="text-sm text-[#5B616E] mt-1">Data subject requests, consent, retention policies</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* DSAR list */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="bg-white border-[#E7E0D6]">
            <CardHeader><CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4" /> Data Subject Requests ({dsars.length})</CardTitle></CardHeader>
            <CardContent>
              {loading ? <Skeleton className="h-32" /> : dsars.length === 0 ? (
                <p className="text-sm text-[#5B616E] text-center py-6">No DSAR requests yet</p>
              ) : (
                <div className="space-y-2">
                  {dsars.map(d => (
                    <div key={d.id} className="flex items-center justify-between p-3 border border-[#E7E0D6] rounded-lg">
                      <div>
                        <p className="text-sm font-medium">{TYPE_LABELS[d.request_type] || d.request_type}</p>
                        <p className="text-xs text-[#5B616E]">{d.email} · {d.submitted_at ? new Date(d.submitted_at).toLocaleDateString() : ''}</p>
                        <p className="text-[10px] text-amber-600">Deadline: {d.deadline_at ? new Date(d.deadline_at).toLocaleDateString() : 'N/A'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge style={{ background: `${STATUS_COLORS[d.status] || '#5B616E'}20`, color: STATUS_COLORS[d.status] || '#5B616E' }} className="text-[10px]">{d.status}</Badge>
                        {d.status === 'pending' && (
                          <Button size="sm" variant="outline" className="text-xs" onClick={() => { setShowProcess(d); setProcessForm({ status: 'completed', notes: '' }); }}>Process</Button>
                        )}
                        {d.user_id && (
                          <Button size="sm" variant="ghost" className="text-xs p-1" onClick={async () => {
                            window.open(`/api/gdpr/export-user-data/${d.user_id}`, '_blank');
                          }}><Download className="w-3.5 h-3.5" /></Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Retention policy */}
        <div className="space-y-4">
          <Card className="bg-white border-[#E7E0D6]">
            <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Clock className="w-4 h-4" /> Data Retention Policy</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {retention && [
                { key: 'analytics_days', label: 'Analytics events (days)' },
                { key: 'sessions_days', label: 'Sessions (days)' },
                { key: 'audit_days', label: 'Audit logs (days)' },
              ].map(f => (
                <div key={f.key}>
                  <label className="text-xs text-[#5B616E] block mb-0.5">{f.label}</label>
                  <Input type="number" value={retentionForm[f.key] || ''} onChange={e => setRetentionForm(r => ({ ...r, [f.key]: parseInt(e.target.value, 10) || 0 }))} className="text-xs" />
                </div>
              ))}
              <Button onClick={saveRetention} disabled={savingRetention} size="sm" className="w-full bg-[#121316] text-white hover:bg-[#1B1D22] text-xs mt-2">
                {savingRetention ? 'Saving…' : 'Save Policy'}
              </Button>
              <Button onClick={purgeExpired} disabled={purging} size="sm" variant="outline" className="w-full text-xs text-red-600 border-red-200 hover:bg-red-50">
                {purging ? 'Purging…' : '🗑 Purge Expired Data'}
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E7E0D6]">
            <CardHeader><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Compliance Reminders</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-xs text-[#5B616E]">
              <p>• DSARs must be completed within <strong>30 days</strong> (GDPR Art. 12)</p>
              <p>• Right to erasure (Art. 17) must be honoured</p>
              <p>• Data minimisation: only collect what's needed</p>
              <p>• Breach notification within <strong>72 hours</strong> to supervisory authority</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Process DSAR dialog */}
      <Dialog open={!!showProcess} onOpenChange={() => setShowProcess(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Process DSAR — {showProcess?.email}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#5B616E]">Request type: <strong>{TYPE_LABELS[showProcess?.request_type] || showProcess?.request_type}</strong></p>
            <Select value={processForm.status} onValueChange={v => setProcessForm(f => ({ ...f, status: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="processing">Mark as Processing</SelectItem>
                <SelectItem value="completed">Mark as Completed</SelectItem>
                <SelectItem value="rejected">Mark as Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Notes / response details..." value={processForm.notes} onChange={e => setProcessForm(f => ({ ...f, notes: e.target.value }))} />
            {processForm.status === 'completed' && showProcess?.request_type === 'erasure' && (
              <p className="text-xs text-[#B3261E] bg-red-50 p-2 rounded-lg flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" /> Completing an erasure request will delete the user's analytics, comments, and consent records.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowProcess(null)}>Cancel</Button>
            <Button onClick={processDSAR} className="bg-[#121316] text-white hover:bg-[#1B1D22]">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
